/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { Button, Tab, Tabs } from '@mui/material';
import { ContainerLayout } from 'components/layouts/ContainerLayout';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { useTranslation } from 'react-i18next';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import useSessionStore from 'stores/useSessionStore';
import { tabs } from 'components/layouts/Tab';
import UtMatrixListPopUp from './popup/UtMatrixListPopUp';
import { UtMatrixSummaryList } from 'models/ut/UtMatrixSummary';
import {
  getDetailSummaryData,
  getElectricSummaryData,
  getMachineSummaryData,
  getEquipmentResultData,
  getUtMatrixSummaryList,
} from 'apis/ut/UtMatrixSummary';
import DetailSummaryContent from './summary/DetailSummaryContent';
import MachineSummaryContent from './summary/MachineSummaryContent';
import ElectricSummaryContent from './summary/ElectricSummaryContent';
import { ComboBox } from 'components/selects/ComboBox';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { fetchElecDetail } from './elec/api';
import { compareElec, withBreakerAnalysis } from './elec/utils/compareElec';
import { ElecDiffRow } from './elec/types';
import { fetchMachineDetail } from './api';
import { analyze } from './utils/analyze';
import { buildRptNm } from './utils/buildRptNm';
import { MIN_COMPARE, UT_TYPES } from './constants';
import UtMatrixCompareTargetPopup, { CompareTarget } from './popup/UtMatrixCompareTargetPopup';
import { AnalysisRow } from './types';
import UtMatrixGraphPopup from './popup/UtMatrixGraphPopup';
import { CommonUtil } from 'utils/CommonUtil';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';
import EquipmentResultContent from './summary/EquipmentResultContent';
import { getExcelFileName } from 'utils/ExcelUtil';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import GridSettingButtons from 'components/grids/GridSettingButtons';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import { ManagementMode } from '../../models/common/Common';
import { UtMatrixOption } from './types';
import {
  requestAiDraftOpinions,
  requestFinalReport,
  saveComparisonReport,
  saveRuleBasedOpinions,
} from 'apis/ut/UtMatrixComparisonReportList';
import { ChatbotButton } from 'components/buttons/ChatbotButton';
import { useChatbotStore } from 'stores/useChatbotStore';

interface SearchParam {
  totalStandard?: string;
}

/**
 * 기계 분석결과(AnalysisRow) → TB_EELMB_UTM_RPT_D 행
 * AnalysisRow 1건은 UT 항목 9종을 함께 들고 있으므로 UT 항목별로 행을 펼치고,
 * UTM_TP_CD에 UT 항목키(CDA/N2/IW…)를 넣는다. 전/후는 1·2번 슬롯에 대응한다.
 * 전·후 모두 값이 없는(0) UT 항목은 저장하지 않는다.
 */
const machineRowsToRptDetail = (rows: AnalysisRow[]) =>
  rows.flatMap((r) =>
    UT_TYPES.map((ut) => {
      const d = r.ut[ut.key];
      if (!d) return null;
      const empty =
        (d.usage[0] || 0) === 0 &&
        (d.usage[1] || 0) === 0 &&
        (d.qty[0] || 0) === 0 &&
        (d.qty[1] || 0) === 0;
      if (empty) return null;
      return {
        prdnProcNm: r.process,
        eqclNm: r.eqGroup,
        dtalProcNm: r.subProcess,
        prdnLnNm: r.line,
        eqpMainNm: r.main,
        eqpMchNm: r.machine,
        utmTpCd: ut.key,
        atc1Qty: d.qty[0] ?? 0,
        atc2Qty: d.qty[1] ?? 0,
        unit1Usg: d.unit[0] ?? null,
        unit2Usg: d.unit[1] ?? null,
        tot1Usg: d.usage[0] ?? null,
        tot2Usg: d.usage[1] ?? null,
        rltDifYn: d.diffU !== 0 || d.diffQ !== 0 ? 'Y' : 'N',
      };
    }).filter(Boolean)
  );

/**
 * @desc UT Matrix 결과보기
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.02   jshwang12       자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리)
 * 2025.10.03   jshwang12       [고도화] 설비별 결과 탭 추가
 * 2025.10.04   jshwang12       UT Matrix Summary 그리드 > N2 AVG 추가
 * 2025.11.05   jihwan89        CSR E20250627-001556-6/27 UT Matrix 화면 개선요청 (결과보기, 결재화면, 작성화면, list화면) summary 엑셀다운로드 추가
 * 2026.08.05   jihwan89        CSR E20260727-000228 Vacuum Pump 추가
 */
const UtMatrixSummaryPage = () => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();
  const machineRef = useRef<any>();
  const elecRef = useRef<any>();
  const detailRef = useRef<any>();
  const eqpRef = useRef<any>();
  const userSession = useSessionStore();
  const location = useLocation();
  const state = { ...location.state };

  const gridRef2 = useRef<any>();
  const [isChangeColumn, setIsChangeColumn] = useState<boolean>(false);

  const [hitTest, setHitTest] = useState<any>();
  const [flexRef, setFlexRef] = useState<any>();
  const [flexItem, setFlexItem] = useState<any>();

  const [summaryRowData, setSummaryRowData] = useState<UtMatrixSummaryList[]>([]);
  const [machineRowData, setMachineRowData] = useState<any>();
  const [electricRowData, setElectricRowData] = useState<any>();
  const [eqpResultRowData, setEqpResultRowData] = useState<any>(); // [25.10.03][고도화] 설비별 결과 탭 추가
  const [detailRowData, setDetailRowData] = useState<any>();

  const [rowDataChange, setRowDataChange] = useState<boolean>(false);

  const [utmData, setUtmData] = useState<UtMatrixSummaryList[]>([]);

  const [isOpenModal, setOpenModal] = useState<boolean>(false);
  const [isOpenGraphModal, setOpenGraphModal] = useState<boolean>(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [isOpenComparison, setOpenComparison] = useState<boolean>(false);
  const [isSavingReport, setIsSavingReport] = useState<boolean>(false);
  const [comparisonMatrices, setComparisonMatrices] = useState<UtMatrixOption[]>([]);
  const MAX_COMPARE = 2;

  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const tabMenus = useMemo(() => {
    return [
      { id: 'MACH', name: t('ut.label.기계요약', '기계요약') },
      { id: 'ELTR', name: t('ut.label.전기요약', '전기요약') },
      { id: 'EQP', name: t('ut.label.설비별 결과', '설비별 결과') },
      { id: 'UTM', name: t('ut.label.상세보기', '상세보기') },
    ];
  }, []);

  const [searchParam, setSearchParam] = useState<SearchParam>({
    totalStandard: 'ALL',
  });
  const [code, setCode] = useState<any>();
  const [selectedTagDivsCd, setSelectedTagDivsCd] = useState<string>('');

  const openChatbot = useChatbotStore((s) => s.openPanel);

  useEffect(() => {
    getCommonCodesForGrid();
  }, []);

  const getCommonCodesForGrid = async () => {
    const sumBaseCd: Code[] = await getCommonCodeNames('SUM_BASE_CD');
    const tagDivsCd: Code[] = await getCommonCodeNames('TAG_DIVS_CD');

    setCode({
      sumBaseCd: sumBaseCd,
      tagDivsCd: tagDivsCd,
    });
  };

  useEffect(() => {
    if ((state.utmData || []).length > 0) {
      callbackUtmData(state?.utmData);
    }
  }, [state.utmData]);

  useEffect(() => {
    if (summaryRowData.length > 0) {
      btnShowResult();
    }
  }, [rowDataChange]);

  const fnSearchJudgeResultBtn = (params: any) => {
    const searchParam = {
      gatingId: params.gatingId,
      gatingNo: params.gatingNo,
    };
    navigate('/gating/result/gating-judge-report', { state: { sJudgeResultParam: searchParam } });
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        minWidth: 40,
        width: 40,
        isReadOnly: true,
        align: 'center',
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'utmId',
        header: String(t('ut.label.UTM ID', 'UTM ID')),
        minWidth: 70,
        width: 95,
        visible: true,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'bildPlntCdNm',
        header: String(t('ut.label.공장', '공장')),
        minWidth: 70,
        width: 100,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'bildPlntCd',
        visible: false,
      },
      {
        binding: 'askPurpCd',
        visible: false,
      },
      {
        binding: 'utmNo',
        header: String(t('ut.label.UT Matrix 번호', 'UT Matrix 번호')),
        minWidth: 70,
        width: 125,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'utmNm',
        header: String(t('ut.label.UT Matrix 명', 'UT Matrix 명')),
        minWidth: 70,
        width: 180,
        isReadOnly: true,
      },
      {
        binding: 'verNo',
        header: String(t('ut.label.버전', '버전')),
        minWidth: 60,
        width: 65,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'lineQty',
        header: String(t('ut.label.Line 수', 'Line 수')),
        minWidth: 70,
        width: 85,
        align: 'center',
        isReadOnly: true,
      },
      {
        binding: 'graph',
        header: String(t('ut.label.Graph', 'Graph')),
        minWidth: 53,
        width: 53,
        align: 'center',
        isReadOnly: true,
        cssClass: 'WijmoPlay',
        cellTemplate: '<button id="btnMoveGraph"></button>',
      },
      {
        header: String(t('ut.label.전기', '전기')),
        columns: [
          {
            binding: 'elec',
            header: '[kW]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.CDA', 'CDA')),
        columns: [
          {
            binding: 'cda',
            header: '[Nl/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.N2 Peak', 'N2 Peak')),
        columns: [
          {
            binding: 'n2',
            header: '[Nl/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.N2 AVG', 'N2 AVG')),
        columns: [
          {
            binding: 'n2Avg',
            header: '[Nl/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.IW', 'IW')),
        columns: [
          {
            binding: 'iw',
            header: '[ℓ/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.FW', 'FW')),
        columns: [
          {
            binding: 'iw2',
            header: '[ℓ/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.PCW', 'PCW')),
        columns: [
          {
            binding: 'pcw',
            header: '[ℓ/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.WW', 'WW')),
        columns: [
          {
            binding: 'ww',
            header: '[ℓ/min]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Steam', 'Steam')),
        columns: [
          {
            binding: 'steam',
            header: '[t/hr]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.NG', 'NG')),
        columns: [
          {
            binding: 'ng',
            header: '[N㎥/hr]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Return air', 'Return air')),
        columns: [
          {
            binding: 'exRetrun',
            header: '[CMH]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Exhaust air', 'Exhaust air')),
        columns: [
          {
            binding: 'exVent',
            header: '[CMH]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      // 26.08.05 CSR E20260727-000228 Vacuum Pump 추가
      {
        header: String(t('ut.label.Vacuum Pump', 'Vacuum Pump')),
        columns: [
          {
            binding: 'vp',
            header: '[CMH]',
            minWidth: 70,
            width: 100,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
      {
        header: String(t('ut.label.Supply Air', 'Supply air')),
        columns: [
          {
            binding: 'sa',
            header: '[CMH]',
            minWidth: 70,
            width: 90,
            align: 'right',
            isReadOnly: true,
            cssClass: 'noMerge',
            cellTemplate: DefaultZeroNumberCellTemplate,
          },
        ],
      },
    ];
  }, [isChangeColumn, flexRef]);

  const onInitialized = (grid) => {
    setFlexRef(grid);
    grid.loadedRows.addHandler(() => {
      const items = grid.collectionView ? grid.collectionView.sourceCollection : grid.itemsSource;
      if (!items || !Array.isArray(items)) {
        return;
      }

      items.forEach((item, rowIndex) => {
        grid.columns.forEach((col, colIndex) => {
          const value = grid.getCellData(rowIndex, colIndex, false);
          const content = CommonUtil.safeHtmlContents(value, false);

          grid.setCellData(rowIndex, colIndex, content, true);
        });
      });
      grid.invalidate();
    });

    grid.hostElement.addEventListener('click', (e) => {
      const len = grid.rows.length;
      if (len == 0) return;
      const ht = grid.hitTest(e);

      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return; // 선택된 영역이 셀이 아니면 나가리.

      if (e.target instanceof HTMLButtonElement) {
        const col = grid.columns[ht.col]; // 선택한 컬럼의 정보
        const item = grid.rows[ht.row].dataItem; // 선택한 row의 정
        setFlexItem(item);
        setHitTest(ht);
        switch (e.target.id) {
          case 'btnOpenModal':
            setOpenModal(true);
            break;
          case 'btnMoveGraph':
            setOpenGraphModal(true);
            setUtmData(item);
            break;
        }
      }
    });

    grid.refresh();
  };

  const callbackUtmData = (utmData) => {
    const utmIds = utmData.map((item) => item.utmId).join(',');

    getUtMatrixSummaryList(utmIds).then((result: UtMatrixSummaryList[]) => {
      if ((result || []).length > 0) {
        const utmData: any = [];

        if (result.length > 10) {
          // utmId 10개 이상 선택 시 10개만 등록하고 메세지로 10개 이상 등록할 수 없습니다.
          const sortUtmId = _.sortBy(result, ['utmId']);
          for (let i = 0; i < 10; i++) {
            utmData.push(sortUtmId[i]);
          }
          setSummaryRowData(utmData);
          openMessageBar({
            type: 'warning',
            content: t(
              'ut.label.Ut Matrix를 추가 할 수 없습니다. (최대 10개)',
              'Ut Matrix를 추가 할 수 없습니다. (최대 10개)'
            ),
          });
        } else if (result.length + summaryRowData.length > 10) {
          // 기존 utmId + new 10개 이상시 utmId 순서대로 10개까지만 등록하고 10개 이상 등록할 수 없습니다.
          const sortUtmId = _.sortBy(result, ['utmId']);
          for (let i = 0; i < 10 - summaryRowData.length; i++) {
            utmData.push(sortUtmId[i]);
          }
          // const newRow = summaryRowData.concat(result);
          const newRow = _.uniqBy(summaryRowData?.concat(utmData), 'utmId');
          setSummaryRowData(newRow);
        } else {
          const newRow = _.uniqBy(summaryRowData?.concat(result), 'utmId');
          const sortUtmId = _.sortBy(newRow, ['utmId']);
          setSummaryRowData(sortUtmId);
        }
        setRowDataChange(rowDataChange ? false : true);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const btnDelRow = () => {
    const selectedRowNodes = flexRef.selectedRows;

    const isSelected = selectedRowNodes.filter((item) => item.isSelected);

    if (isSelected.length < 1) {
      openMessageBar({
        type: 'error',
        content: t('ut.label.Ut Matrix를 선택해 주세요.', 'Ut Matrix를 선택해 주세요.'),
      });
      return false;
    }
    const selectedIds = selectedRowNodes
      .map((rowNode) => {
        return parseInt(rowNode._idx!);
      })
      .reverse();

    selectedIds.forEach((idx) => {
      delete summaryRowData[idx];
    });
    const filteredData = summaryRowData.filter((element) => element !== undefined);
    const sortUtmId = _.sortBy(filteredData, ['utmId']);
    setSummaryRowData(sortUtmId);
    setRowDataChange(rowDataChange ? false : true);
  };

  const btnShowResult = () => {
    if ((summaryRowData || []).length < 1) {
      openMessageBar({
        type: 'error',
        content: t('ut.label.UT Matrix를 추가해 주세요.', 'UT Matrix를 추가해 주세요.'),
      });
      return false;
    }
    const utmIds = summaryRowData.map((item) => item.utmId).join(',');
    getMachineSummaryData(utmIds, searchParam).then((result) => {
      if (result !== null) {
        setMachineRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
    getElectricSummaryData(utmIds, searchParam).then((result) => {
      if (result !== null) {
        setElectricRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
    getEquipmentResultData(utmIds, searchParam).then((result) => {
      if (result !== null) {
        setEqpResultRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
    getDetailSummaryData(utmIds).then((result) => {
      if (result !== null) {
        setDetailRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const btnExcelDownload = () => {
    if (currentTabIndex === 0) {
      machineRef.current?.btnMachineExcelExport();
    } else if (currentTabIndex === 1) {
      elecRef.current?.btnElecExcelExport();
    } else if (currentTabIndex === 2) {
      // [25.10.03][고도화] 설비별 결과 탭 추가
      eqpRef.current?.btnDetailExcelExport();
    } else if (currentTabIndex === 3) {
      detailRef.current?.btnDetailExcelExport();
    }
  };

  const btnMasterExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = 'Ut Matrix Summary';
    book.saveAsync(getExcelFileName(t('ut.label.UT Matrix Summary', 'UT Matrix Summary')));
  };

  /** 정합화 Report 버튼 — 비교 대상 선택 팝업을 연다 (리포트 생성은 팝업 확정 후) */
  const btnOpenComparison = () => {
    const checkedRows = (flexRef?.selectedRows || [])
      .filter((row) => row.isSelected)
      .map((row) => row.dataItem as UtMatrixSummaryList)
      .filter((item) => !!item?.utmId);
    const candidates = checkedRows.length > 0 ? checkedRows : summaryRowData || [];

    if (candidates.length < MIN_COMPARE) {
      openMessageBar({
        type: 'error',
        content: t(
          'ut.label.비교할 UT Matrix를 2개 이상 추가해 주세요.',
          '비교할 UT Matrix를 2개 이상 추가해 주세요.'
        ),
      });
      return false;
    }

    setComparisonMatrices(candidates.map(toUtMatrixOption));
    setOpenComparison(true);
  };

  /**
   * 비교 대상 확정 — 기계/전기 분석을 모두 수행해 리포트 1건으로 저장한다.
   * 분석은 기준(1번) ↔ 비교대상(2번)만 사용하고, 참조(3번)는 대상 목록에만 남는다.
   */
  const handleCompareTargetConfirm = async (selected: CompareTarget[]) => {
    const rptNm = buildRptNm(selected[0], selected[1]);
    // 분석 대상은 기준·비교대상 2건. 참조는 상세 분석에 포함하지 않는다.
    const utmIds = [selected[0].utmId, selected[1].utmId];

    setIsSavingReport(true);
    try {
      const [machRows, bfElec, afElec] = await Promise.all([
        fetchMachineDetail(utmIds),
        fetchElecDetail([utmIds[0]]),
        fetchElecDetail([utmIds[1]]),
      ]);

      const machResult = analyze({ utmIds, rows: machRows, lineInfo: [], perLine: false });
      // 차단기 판정(부족/적정/과다)은 원천의 역률·전압이 있어야 계산되는데 RPT_ELPW_D에는
      // 그 값이 없다. 저장 시점에 문구를 붙여 보내야 나중에 여유율 집계를 할 수 있다.
      const elecDiffRows: ElecDiffRow[] = withBreakerAnalysis(compareElec(bfElec, afElec));

      // 동일 대상·동일 순서 리포트가 이미 있으면 BE가 새로 만들지 않고
      // 기존 RPT_ID를 duplicated=true로 돌려준다
      const {
        rptId,
        duplicated,
        rptNm: dupRptNm,
      } = await saveComparisonReport({
        rptNm,
        userId: userSession.userId,
        selectedMatrices: selected,
        machDiffRows: machineRowsToRptDetail(machResult.rows),
        elecDiffRows,
      });

      // TODO(BE 연동 완료 후) — 원래는 신규 생성일 때만(!duplicated) 요청해야 한다.
      // 중복 리포트에는 이미 초안이 있고 사용자가 의견을 써놨을 수 있기 때문(v2 Q3).
      // BE 연동 테스트 중에는 같은 조합으로 반복 호출해야 해서 조건을 임시로 뺀다.
      requestAiDraftOpinions(rptId, rptNm, utmIds);

      // 규칙 기반 자동 생성 — 전기 종합분석 A-0001/A-0002 + 기계 UT 항목별 분석결과(⑥) 9종.
      // 전기 입력은 기준(before) 원천 행 전체, 기계 입력은 비교 분석 결과다.
      // A-0003(훅업 포인트 간소화)은 BE가 리포트 생성 시 직접 넣으므로 여기서 다루지 않는다.
      //
      // ⚠ 전기·기계를 한 호출로 저장한다 — saveRptOpinions가 의견 행을 통째로 덮어쓰기 때문에
      //   나눠 부르면 뒤에 실행되는 쪽이 앞서 넣은 행을 조용히 지운다(실제로 전기 2항목이 유실됐다).
      const ruleSaved = await saveRuleBasedOpinions(
        rptId,
        bfElec,
        machResult.rows,
        selected[1].utmId,
        userSession.userId
      );
      // 실패해도 리포트 자체는 만들어졌으므로 진행은 계속하되, 빈 칸으로 남는다는 걸 알려준다
      if (!ruleSaved.ok) {
        openMessageBar({
          type: 'error',
          content: `분석 결과 자동 생성 저장에 실패했습니다. 종합 분석 항목이 비어 있을 수 있습니다. (${
            ruleSaved.error ?? '원인 불명'
          })`,
        });
      }

      if (duplicated) {
        // 같은 대상으로 다시 실행한 것 = 재저장. 이때 최종 리포트 AI를 호출한다
        // (ref/html/ut_matrix_rpt_demo.html 219줄 "재저장 시 /report/final 로 ai 호출")
        requestFinalReport(rptId, dupRptNm || rptNm, {
          baseUtmId: selected[0].utmId,
          comparedUtmId: selected[1].utmId,
          referUtmId: selected[2]?.utmId,
        });

        // 기존 리포트의 분석명은 BE가 내려줄 때만 노출한다 (FE가 만든 rptNm은 신규용 이름이라 다를 수 있음)
        const baseMsg = t(
          'ut.label.동일한 대상으로 생성된 리포트가 이미 있어 기존 리포트를 표시합니다.',
          '동일한 대상으로 생성된 리포트가 이미 있어 기존 리포트를 표시합니다.'
        );
        openMessageBar({
          type: 'warning',
          content: dupRptNm ? `${baseMsg} (${dupRptNm})` : String(baseMsg),
        });

        // 이미 있는 리포트이므로 목록을 거치지 않고 분석 화면(기계/전기 탭)으로 바로 이동
        navigate('/ut/comparison-report/detail', {
          state: {
            upprMnuUrl: location.pathname,
            rptId,
            rptNm: dupRptNm || rptNm,
            mnuNm: t('ut.label.UT Matrix 정합화 상세 분석', 'UT Matrix 정합화 상세 분석'),
          },
        });
        return;
      }

      navigate('/ut/comparison-report/list', {
        state: {
          upprMnuUrl: location.pathname,
          mnuId: `/ut/comparison-report/list`,
          mnuNm: t('ut.label.UT Matrix 정합화 list', 'UT Matrix 정합화 list'),
          rptId,
        },
      });
    } catch (e) {
      openMessageBar({
        type: 'error',
        content: t(
          'ut.label.리포트 생성 중 오류가 발생했습니다.',
          '리포트 생성 중 오류가 발생했습니다.'
        ),
      });
    } finally {
      setIsSavingReport(false);
    }
  };

  /** UT Matrix Summary 그리드 행 → UT 사용량 비교 화면의 비교 대상 형식 */
  const toUtMatrixOption = (row: UtMatrixSummaryList): UtMatrixOption => ({
    utmId: String(row.utmId ?? ''),
    utmNo: String(row.utmNo ?? ''),
    verNo: String(row.verNo ?? ''),
    utmNm: String(row.utmNm ?? ''),
    statusNm: '', // UtMatrixSummaryList에는 상태 필드가 없어 비교 화면에서는 '-'로 표기
  });

  return (
    <ContainerLayout>
      {isSavingReport && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                width: 48,
                height: 48,
                border: '4px solid #e0e0e0',
                borderTopColor: '#2d9bb2',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                margin: '0 auto 12px',
              }}
            />
            <div style={{ color: '#2d9bb2', fontSize: 14, fontWeight: 500 }}>리포트 생성 중...</div>
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      <SubTitleLayout style={{ marginTop: '0' }}>
        <SubTitleGroup>
          <h3>{t('ut.label.UT Matrix Summary', 'UT Matrix Summary')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(summaryRowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="addRow"
            onClick={() => setOpenModal(true)}
            disableRipple
          >
            {t('com.button.행추가', '행추가')}
          </Button>
          <Button css={IconButton.button} className="delRow" onClick={btnDelRow} disableRipple>
            {t('com.button.행삭제', '행삭제')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnMasterExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '다운로드')}
          </Button>
          <GridSettingButtons
            gridId={'utMatrixSummaryResultGrid'} // <CustomGrid>에 선언한 id와 동일한 값
            layoutDefinition={layoutDefinition} // 그리드 레이아웃
            changeShownColumns={(result) => {
              // 노출필드 변경이벤트
              // ref를 이용해 <CustomGrid> 내 함수를 실행
              //openLoading(true);
              gridRef2.current?.changeColumn?.(result);
              setIsChangeColumn(true);
              flexRef.invalidate();
              //gridRef.current.refresh();
              //openLoading(false);
            }}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        id={'utMatrixSummaryResultGrid'}
        ref={gridRef2}
        layoutDefinition={layoutDefinition}
        rowData={summaryRowData}
        initialized={onInitialized}
        showMarquee={true}
        height={130}
        isFilter={false}
        excludePin={[
          'graph',
          'elec',
          'cda',
          'n2',
          'n2Avg',
          'iw',
          'iw2',
          'pcw',
          'ww',
          'steam',
          'ng',
          'exRetrun',
          'exVent',
          'vp',
          'sa',
        ]}
      />
      <SubTitleLayout style={{ marginTop: '28px' }}>
        <SubTitleGroup>
          <div css={tabs.wrap} style={{ marginTop: '24px' }}>
            <Tabs
              value={currentTabIndex}
              onChange={(e, newValue) => setCurrentTabIndex(newValue)}
              css={tabs.tab}
            >
              {tabMenus.map((o, idx) => (
                <Tab
                  key={o.id}
                  label={o.name}
                  id={`ut-tab-${idx}`}
                  aria-controls={`ut-tabpanel-${idx}`}
                />
              ))}
            </Tabs>
          </div>
          <span className="total">
            {String(t('com.label.총', '총'))}
            <span>
              {(
                (currentTabIndex === 0
                  ? machineRowData?.list
                  : currentTabIndex === 1
                  ? electricRowData?.list
                  : detailRowData) || []
              ).length.toLocaleString()}
            </span>
            {String(t('com.label.건', '건'))}
          </span>
        </SubTitleGroup>

        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="icoNone"
            startIcon={<PrecisionManufacturingIcon />}
            onClick={btnOpenComparison}
            disableRipple
          >
            정합화 Report
          </Button>
          <div
            style={{
              width: '1px',
              height: '20px',
              background: '#d0d0d0',
              margin: '0 12px',
            }}
          />
          <label>{String(t('ut.label.집계기준', '집계기준'))}</label>
          <ComboBox
            options={code?.sumBaseCd}
            defaultValue={searchParam.totalStandard}
            onChange={(value) => {
              setSearchParam({
                ...searchParam,
                totalStandard: value,
              });
            }}
          />
          <Button css={IconButton.button} className="request" onClick={btnShowResult} disableRipple>
            {String(t('com.button.결과보기', '결과보기'))}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelDownload}
            disableRipple
          >
            {String(t('com.button.다운로드', '다운로드'))}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <TabPanel value={currentTabIndex} index={currentTabIndex}>
        {currentTabIndex === 0 ? (
          <MachineSummaryContent ref={machineRef} machineRowData={machineRowData} />
        ) : currentTabIndex === 1 ? (
          <ElectricSummaryContent ref={elecRef} electricRowData={electricRowData} />
        ) : currentTabIndex === 2 ? (
          <EquipmentResultContent ref={eqpRef} eqpResultRowData={eqpResultRowData} />
        ) : currentTabIndex === 3 ? (
          <DetailSummaryContent ref={detailRef} detailRowData={detailRowData} />
        ) : (
          ''
        )}
      </TabPanel>

      <ChatbotButton onClick={() => openChatbot('ut-matrix')} />

      {isOpenModal && (
        <UtMatrixListPopUp
          open={isOpenModal}
          close={() => setOpenModal(false)}
          onCallback={(callbackData) => callbackUtmData(callbackData)}
        />
      )}
      {isOpenGraphModal && (
        <UtMatrixGraphPopup
          open={isOpenGraphModal}
          close={() => setOpenGraphModal(false)}
          condition={utmData}
        />
      )}
      {isOpenComparison && (
        <UtMatrixCompareTargetPopup
          open={isOpenComparison}
          close={() => setOpenComparison(false)}
          matrices={comparisonMatrices}
          onCallback={handleCompareTargetConfirm}
        />
      )}
    </ContainerLayout>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
  const { children, value, index } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`ut-tabpanel-${index}`}>
      {value === index && <div>{children}</div>}
    </div>
  );
}

export default UtMatrixSummaryPage;
